(ns manage.adset-table.view
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["@mui/material/Typography$default" :as Typography]
            ["@mui/x-data-grid-premium" :refer [DataGridPremium useGridApiRef]]
            ["moment/min/moment-with-locales$default" :as moment]
            ["src/svg/inform/plus.svg$default" :as Plus]
            ["src/svg/wpp/arrows/trend-ascent.svg$default" :as TrendAscent]
            [cljs-bean.core :refer [->clj]]
            [clojure.string :as str]
            [common.flags :refer [use-flag]]
            [common.localstorage :as localstorage]
            [common.modal.dialog :as dialog]
            [common.modal.util :refer [with-confirmation-modal]]
            [common.modal.view :refer [defmodal]]
            [common.patterns.stats-daterange-select :refer [daterange-select
                                                            get-initial-daterange
                                                            recent-stats-daterange]]
            [common.queries :refer [use-queries use-query]]
            [common.resource.query.adset :as q]
            [common.resource.query.campaign :as campaign-queries]
            [common.util :as util]
            [common.widgets.column-renderers :refer [template-dimensions-renderer]]
            [common.widgets.data-grid :as dg]
            [manage.adset.business-logic :as business-logic]
            [manage.adset.util :as adset-util]
            [manage.campaign.hooks :as ch]
            [mate.core :as mc]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defn- format-ctr
  [{:keys [ctr channel]
    :or {ctr 0}}]
  (if (= channel "youtube")
    "N/A"
    (str (util/->locale-string (* 100 ctr) {:minimumFractionDigits 3}) "%")))

(defui format-name
  [{:keys [name archived channel]}]
  ($ Stack
     ($ Typography
        (cond-> {:noWrap true}
          archived (assoc :sx #js {:color "text.disabled"})) name)
     (when channel
       ($ Typography
          {:fontSize "sm"
           :sx #js {:color "grey.20"}}
          (get-in business-logic/channels [(keyword channel) :label])))))

(defui format-status-with-tooltip [{:keys [status tooltip]}]
  ($ Tooltip
     {:placement "top"
      :title tooltip}
     ($ Chip
        (cond-> {:label status
                 :variant "subtle"
                 :sx #js {:textTransform "capitalize"}}
          (= status "active") (assoc :color "success")))))

(defn status-tooltip
  ([entity status]
   (case status
     "archived" (str "This " entity " has been archived")
     "active" (str "This " entity " received impressions/was created in the last 28 days")
     "inactive" (str "This " entity " did not receive impressions in the last 28 days")))
  ([entity status channel]
   (if (contains? #{"youtube" "meta"} channel)
     (case status
       "archived" (str "This " entity " has been archived")
       "active" (str "This " entity " was created in the last 28 days")
       "inactive" (str "This " entity " was created more than 28 days ago"))
     (status-tooltip entity status))))

(defui optimizing-chip []
  ($ Chip
     {:color "success"
      :variant "subtle"
      :label "Optimizing"
      :icon ($ TrendAscent)}))

(defui format-status [{:keys [channel status id]}]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {{:keys [test-state]} :data} (use-query (q/adset-aco-active-configuration
                                                 {:advertiser-id advertiser-id
                                                  :adset-id id}))]
    (cond
      (= test-state "started") ($ optimizing-chip)
      status ($ format-status-with-tooltip
                {:status status
                 :tooltip (status-tooltip "adset" status channel)}))))

(defui format-impressions [{:keys [impressions channel targetImpressions]}]
  (if (= channel "youtube")
    "N/A"
    ($ Stack
       ($ Typography (or (util/->locale-string impressions) "0"))
       (when targetImpressions
         ($ Typography
            {:fontSize "sm"
             :sx #js {:color "grey.20"}}
            "of " (util/->locale-string targetImpressions))))))

(defn- format-clicks [{:keys [clicks channel]}]
  (if (= channel "youtube")
    "N/A"
    (or (util/->locale-string clicks) "0")))

(defui format-updated-at [{:keys [updated-at]}]
  ($ Tooltip
     {:placement "top"
      :title (util/format-timestamp updated-at)}
     ($ Typography
        {:component "span"}
        (.fromNow (moment (* 1000 updated-at))))))

(defn derive-status
  ([archived]
   (when archived "archived"))
  ([archived impressions-recent createdAt]
   (let [now (moment)
         created-at (moment (* 1000 createdAt))]
     (cond
       archived "archived"
       (or (pos? impressions-recent)
           (< (.diff now created-at "days") 28)) "active"
       :else "inactive"))))

(defn enrich-status
  [{:keys [archived impressions-recent createdAt] :as adset}]
  (assoc adset :status (derive-status archived impressions-recent createdAt)))

;; This modal warns the user that some selected adsets have warnings before downloading/exporting them.
(defmodal adsets-with-warnings ::adsets-with-warnings
  [{:keys [on-submit
           submit-button-label
           selected-adset-count
           adsets-with-warnings-count]}]
  [dialog/form-dialog
   {:modifier :warning
    :title "Are you sure?"
    :submit-button-testid "confirm"
    :submit-button-label submit-button-label
    :on-submit on-submit}
   [:> Typography
    {:sx {:minWidth "30rem"
          :fontWeight "semibold"}}
    (if (< selected-adset-count 2)
      "The adset you selected has warning(s)."
      (str adsets-with-warnings-count " of the "
           selected-adset-count " adsets you selected "
           (if (= adsets-with-warnings-count 1) "has" "have")
           " warning(s)."))]])

(defui export-tag-menu [{:keys [selected-adset-ids adsets]}]
  (let [adset-by-id (mc/index-by :id adsets)
        adsets-with-warnings (->> selected-adset-ids
                                  (map adset-by-id)
                                  (filterv (comp seq :warnings)))]
    ($ Button
       {:variant "text"
        :onClick (fn [^js _event]
                   (with-confirmation-modal (seq adsets-with-warnings)
                     ::adsets-with-warnings
                     {:submit-button-label "Generate anyway"
                      :selected-adset-count (count selected-adset-ids)
                      :adsets-with-warnings-count (count adsets-with-warnings)}
                     #(rf/dispatch [:export-tags/open-ocp :html-file selected-adset-ids])))
        :data-testid "exportTagsMenuButton"}
       "Generate tags")))

(defui new-adset-button []
  (let [adset-creation-ocp? (use-flag :adset-creation-ocp)]
    ($ Button
       {:variant "contained"
        :onClick #(rf/dispatch (if adset-creation-ocp?
                                 [:ocp/open {:id :adset/create-adset}]
                                 [:modal/open {:id :adset/create-adset}]))
        :startIcon ($ Plus)}
       "New adset")))

(defn- get-adset-dimensions
  "Extract all dimensions from a row"
  [{:keys [workflow templateRevisions adBuilders]}]
  (let [templates (if (= workflow "ad-variants")
                    (mapcat :ad-formats adBuilders)
                    templateRevisions)]
    (->> templates
         (map #(select-keys % [:width :height]))
         set
         (into []))))

(defn- get-dimensions-filter-options
  "Get all unique dimensions from the dataset for filter options"
  [data]
  (->> data
       (mapcat get-adset-dimensions)
       set
       (mapv #(hash-map :value % :label (str (:width %) "x" (:height %))))))

(def Toolbar
  (uix/as-react
   (fn [{:keys [selected-adset-ids adsets daterange set-daterange quick-filter-props]}]
     ($ dg/grid-toolbar {:quick-filter-props quick-filter-props}
        ($ Stack {:direction "row" :sx #js {:flex 1}}
           ($ dg/filters-button
              {:filters [{:id "channel"
                          :label "Channel"
                          :field "channel"
                          :filter-options (ch/use-campaign-channel-options)}
                         {:id "status"
                          :label "Status"
                          :field "status"
                          :filter-options (->> ["archived" "active" "inactive"]
                                               (mapv #(hash-map :value % :label (str/capitalize %))))}
                         {:id "dimensions"
                          :label "Dimensions"
                          :field "dimensions"
                          :operator "collContainsSome"
                          :filter-options (get-dimensions-filter-options adsets)}]}))
        ($ Stack
           {:direction "row"
            :gap 1}
           (if (seq selected-adset-ids)
             ($ export-tag-menu
                {:selected-adset-ids selected-adset-ids
                 :adsets adsets})
             ($ daterange-select {:value daterange :on-change set-daterange}))
           ($ new-adset-button))))))

(def grid-columns
  #js [#js {:field "adName"
            :headerName "Adset name"
            :type "string"
            :minWidth 250
            :flex 2
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:name row))
            :renderCell (fn [^js params]
                          ($ format-name (.-row params)))}
       #js {:field "channel"
            :headerName "Channel"
            :hideable false
            :type "singleSelect"
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:channel row))}
       #js {:field "dimensions"
            :headerName "Dimensions"
            :minWidth 100
            :flex 1
            :headerAlign "center"
            :align "center"
            :valueGetter (fn [_value row]
                           (get-adset-dimensions row))
            :renderCell (fn [^js params]
                          (let [width (.. params -colDef -computedWidth)]
                            ($ template-dimensions-renderer
                               {:templates-dimensions (.-value params)
                                :width width})))
            :filterOperators #js [dg/coll-contains-some-operator]}
       #js {:field "impressions"
            :headerName "Impressions"
            :type "number"
            :width 100
            :flex 1
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:impressions row))
            :renderCell (fn [^js params]
                          (let [{:keys [impressions channel targetImpressions]} (.-row params)]
                            ($ format-impressions {:impressions impressions
                                                   :channel channel
                                                   :targetImpressions targetImpressions})))}
       #js {:field "clicks"
            :headerName "Clicks"
            :type "number"
            :width 100
            :flex 1
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:clicks row))
            :renderCell (fn [^js params]
                          (format-clicks (.-row params)))}
       #js {:field "ctr"
            :headerName "CTR"
            :type "number"
            :width 100
            :flex 1
            :headerAlign "center"
            :align "center"
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:ctr row))
            :renderCell (fn [^js params]
                          (format-ctr (.-row params)))}
       #js {:field "lastModified"
            :headerName "Last activity"
            :type "dateTime"
            :minWidth 150
            :flex 1
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (js/Date. (:updatedAt row)))
            :renderCell (fn [^js params]
                          ($ format-updated-at {:updated-at (.-value params)}))}
       #js {:field "status"
            :headerName "Status"
            :type "singleSelect"
            :minWidth 130
            :valueOptions #js ["archived" "active" "inactive"]
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:status row))
            :renderCell (fn [^js params]
                          ($ format-status (.-row params)))}])

(defn get-recent-impressions [^js data]
  (->> data
       ->clj
       (mapv (fn [{:keys [adset-id impressions]}]
               {:id adset-id
                :impressions-recent impressions}))))

(defn combine-queries-results [^js results]
  (let [results (->clj results)
        [{adsets :data} {stats :data} {recent-stats :data} {warnings :data}] results]
    #js {:isPending (boolean (some :isPending results))
         :data (->> (cond-> adsets
                      (seq stats) (util/left-join stats :id)
                      (seq recent-stats) (util/left-join recent-stats :id)
                      (seq warnings) (util/left-join warnings :id))
                    (mapv enrich-status))}))

(defhook use-adset-data [{:keys [advertiser-id campaign-id daterange]}]
  (let [{campaign :data} (use-query (campaign-queries/campaign-query {:campaign-id campaign-id}))

        adsets-query (q/adsets-query {:campaign-id campaign-id})
        stats-query (q/adsets-stats-query {:campaign-id campaign-id :daterange daterange})
        recent-stats-query (util/spread-obj (q/adsets-stats-query {:campaign-id campaign-id :daterange recent-stats-daterange})
                                            #js {:select get-recent-impressions})
        warnings-query (q/adsets-warnings-query {:advertiser-id advertiser-id :adset-ids (:adset2Ids campaign)})]
    (use-queries #js {:queries #js [adsets-query stats-query recent-stats-query warnings-query]
                      :combine combine-queries-results})))

(defui adset-data-grid []
  (let [{:keys [advertiser-id campaign-id] :as url-context} (use-subscribe [:url-context])
        api-ref (useGridApiRef)

        column-dimensions (localstorage/get "adset-table-column-dimensions")

        [selection set-selection] (uix/use-state #js [])

        [daterange set-daterange] (uix/use-state (get-initial-daterange))
        {:keys [data isFetching]} (use-adset-data {:advertiser-id advertiser-id :campaign-id campaign-id :daterange daterange})

        get-row-id (uix/use-callback (fn [row]
                                       (:id row))
                                     [])

        page-size 10
        initial-state (uix/use-memo (fn []
                                      (clj->js
                                       (cond-> {:pagination {:paginationModel {:pageSize page-size}}
                                                :columns {:columnVisibilityModel {"channel" false}}
                                                :filter {:filterModel {:quickFilterExcludeHiddenColumns false
                                                                       :items [{:id "status"
                                                                                :field "status"
                                                                                :operator "isAnyOf"
                                                                                :value ["active" "inactive"]}]}}
                                                :sorting {:sortModel [{:field "lastModified" :sort "desc"}]}}
                                         column-dimensions (assoc-in [:columns :dimensions] column-dimensions))))
                                    [page-size column-dimensions])

        save-snapshot (uix/use-callback (fn []
                                          (let [current-state (->clj (.exportState (.-current api-ref)))
                                                dimensions (get-in current-state [:columns :dimensions])]
                                            (localstorage/save! "adset-table-column-dimensions" dimensions)))
                                        [api-ref])]
    (uix/use-layout-effect (fn []
                             (.addEventListener js/window "beforeunload" save-snapshot)

                             (fn []
                               (.removeEventListener js/window "beforeunload" save-snapshot)
                               (save-snapshot)))
                           [save-snapshot])
    ($ DataGridPremium
       {:apiRef api-ref
        :columns grid-columns

        :loading isFetching

        :rows (to-array data)
        :getRowId get-row-id

        :checkboxSelection true
        :disableRowSelectionOnClick true
        :keepNonExistentRowsSelected true
        :rowSelectionModel selection
        :onRowSelectionModelChange set-selection

        :pagination true
        :pageSizeOptions #js [page-size]

        :initialState initial-state

        :slots #js {:toolbar Toolbar
                    :cell dg/WithLinkCell}
        :slotProps #js {:baseTextField dg/grid-toolbar-outlined-input
                        :filterPanel #js {:data-testid "filterPanel"
                                          :filterFormProps #js {:data-testid "filterForm"
                                                                :columnInputProps #js {:data-testid "columnInput"}
                                                                :operatorInputProps #js {:data-testid "operatorInput"}
                                                                :valueInputProps #js {:data-testid "valueInput"}}}
                        :toolbar #js {:quick-filter-props {:inputProps #js {:data-testid "searchInput"}}
                                      :selected-adset-ids selection
                                      :adsets data
                                      :daterange daterange
                                      :set-daterange set-daterange}
                        :cell #js {:href-fn (fn [{:keys [workflow id]}]
                                              (adset-util/landing-url-from-workflow workflow
                                                                                    (assoc url-context :adset-id id)))}}
        :noWrap true
        :sx #js {:maxHeight "70rem"}})))
